import { useEffect, useState } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { productSelectorData, saveCustomerAnswers as saveAnswersMutation } from 'services/productSelectorData'
import { getPromotionForCart } from 'services/addPromotionToCart'
import { Toast } from 'lib/ui/components'
import { PRODUCT_SELECTOR_CODE } from 'app/livbrite/resources/constants'
import { MagentoMediaGalleryEntry, MagentoMoney } from 'lib/types'

interface Props {
  questionId?: number
  questionnaireCode: string
  answerId?: number
}

export interface Answers {
  id: string
  question: string
  value: string
  label: string
}
interface Option {
  id: number
  label: string
  options?: Record<string, any>
}
export interface Question {
  id: number
  rowId: number
  title: string
  options: Option[]
  answers: Answer[]
  type: string
}
interface Answer {
  id: number
  label: string
  value?: string
}

export interface ProductResult {
  sku: string
  id: number
  name: string
  url_key: string
  image: {
    label: string
    url: string
  }
  visiture_custom_attributes: string
  price_range: {
    maximum_price: {
      final_price: MagentoMoney
    }
  }
  media_gallery_entries: MagentoMediaGalleryEntry[]
}
export interface Result {
  sku: string
  manufacturer: string
  primary_bladder_leakage: string
  url_key: string
  uses_by_day: string
  product: ProductResult
}

export interface RecommendationsResult {
  learn_more?: string
  name: string
  view_related_products?: string
}

const questions: Question[] = []
const results: Result[] = []

export const useProductSelector = (props: Props) => {
  const history = useHistory()
  const { questionId, questionnaireCode, answerId } = props
  const isWellnessSurvey = questionnaireCode === PRODUCT_SELECTOR_CODE.SURVEY
  const [step, setStep] = useState<number>(0)
  const [totalQuestions, setTotalQuestions] = useState<number | undefined>()
  const [showResults, setShowResults] = useState(false)
  const [noQuestions, setNoQuestions] = useState(false)
  const [saveAnswer, { data: result, loading: savingAnswer }] = useMutation(saveAnswersMutation)
  const [customerEmail, setCustomerEmail] = useState<string | undefined>()
  const resultsData = result?.psSaveCustomerQuestionnaire?.result
  const [getQuestion, { loading, error, data }] = useLazyQuery(productSelectorData, {
    onCompleted: data => {
      const questionData = data?.psGetNextQuestion.question
      if (data.psGetNextQuestion.total_questions !== 0) {
        setTotalQuestions(data.psGetNextQuestion.total_questions)
      }

      if (questionData) {
        questions.push({
          answers: [],
          id: questionData.id,
          rowId: questionData.row_id,
          title: questionData.title,
          type: questionData.type,
          options: questionData.options.map((option: any) => {
            const options = option?.child_options?.map((child: any) => ({ id: child.value, label: child.label }))

            return {
              id: option.value,
              label: option.label,
              options
            }
          })
        })
      } else {
        if (step === 0) {
          setNoQuestions(true)
        }
      }
    }
  })
  const questionData = data?.psGetNextQuestion.question
  const resultsReady = data?.psGetNextQuestion.result

  useEffect(() => {
    getQuestion(
      {
        variables: {
          questionId,
          questionnaireCode,
          answerId
        }
      }
    )
    if (questions.length) {
      setStep(1)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const resultErrorMessage = result?.psSaveCustomerQuestionnaire?.message
    if (resultsData && !isWellnessSurvey) {
      if (resultsData?.length > 0) {
        results.push(...resultsData)
        history.push(`/product-selector/results/${results[0].manufacturer}/${results[0].primary_bladder_leakage}/${results[0].product.sku}`)
      } else {
        history.push('/product-selector/no-results')
      }
    } else if (resultErrorMessage) {
      history.push('/product-selector/no-results')
      // eslint-disable-next-line
      console.error('Error', resultErrorMessage)
    }
    // eslint-disable-next-line
  }, [resultsData, result])

  useEffect(() => {
    if (results.length > 0 && !showResults) {
      results.splice(0, results.length)
    }
    // eslint-disable-next-line
  }, [])

  function getNextQuestion (questionId: number, answerId: number) {
    getQuestion({
      variables: {
        questionId,
        questionnaireCode,
        answerId
      }
    })
  }

  function getFirstQuestion () {
    getQuestion({
      variables: {
        questionnaireCode
      }
    })
  }

  const submitAnswer = (answerValues: Record<string, any>) => {
    const questionId = questions[step].id
    const optionsKeys = Object.keys(answerValues)
    const optionId = answerValues[questionId]
    const questionTypeText = questionData.type === 'text'
    const options = questionTypeText ? optionsKeys : optionId
    const optionsIsArray = Array.isArray(options)

    if (optionsIsArray) {
      options.map((option: string, i: number) => {
        const optionNumber = parseInt(option)
        const optionData: Option | undefined = questions[step].options.find(el => el.id === optionNumber)

        const childOptionData: Option | undefined = questions[step].options.reduce((result, option) => {
          if (option.options?.length > 0) {
            const childOption = option.options?.find((child: Record<string, any>) => child.id === optionNumber)
            if (childOption) {
              result = {
                id: childOption.id,
                label: `${option.label}: ${childOption.label}`
              }
            }
          }
          return result
        }, { id: 0, label: '' })
        if (optionData ?? childOptionData?.id !== 0) {
          const value = answerValues[`${optionData?.id ?? childOptionData?.id}-other`] ?? optionsKeys[0]

          return questions[step].answers.push({
            id: optionData?.id ?? childOptionData?.id,
            label: optionData?.label ?? childOptionData?.label,
            value: !questionTypeText ? value : answerValues[optionsKeys[i]]
          })
        }
        return false
      })
    } else {
      const optionData: Option | undefined = questions[step].options.find(el => el.id === parseInt(options))
      questions[step].answers.push({
        id: optionData?.id ?? 0,
        label: optionData?.label ?? '',
        value: optionsKeys[0]
      })
    }

    if (!resultsReady) {
      setStep(step + 1)
      getNextQuestion(questionId, parseInt(optionId ?? optionsKeys))
    } else {
      setShowResults(true)
    }
  }

  const stepBack = (currentQuestion: Record<string, any>) => {
    const len = questions.length
    const currentQuestionIndex = questions.findIndex(question => question.id === currentQuestion.id)
    const prevQuestionIndex = (currentQuestionIndex+len-1)%len
    const prevAnswer = questions[prevQuestionIndex]
    questions.splice(prevQuestionIndex + 1, questions.length - (prevQuestionIndex + 1))

    if (currentQuestionIndex === 0) {
      questions.length = 0
      getFirstQuestion()
      setStep(0)
    } else {
      getNextQuestion(prevAnswer.id, prevAnswer.answers[0].id)
      setStep(currentQuestionIndex)
    }
  }

  const saveCustomerAnswers = () => {
    const finalAnswers: any = []
    questions?.map(option => {
      const answers = option.answers

      return answers?.forEach((answer: Answer) => {
        finalAnswers.push(
          {
            row_id: option.rowId,
            question_id: option.id,
            question_label: option.title,
            option_id: answer.id,
            option_label: answer.label,
            option_value: answer.value
          }
        )
      })
    })

    saveAnswer({
      variables: {
        answers: finalAnswers,
        questionnaireCode,
        customerEmail
      }
    })
      .then(() => {
        setShowResults(true)
        questions.length = 0
        setStep(0)
      })
      .catch(() => {
        Toast.toastFailure({ messageId: 'app.whoops' })
      })
  }

  return {
    questionData,
    totalQuestions,
    resultsReady,
    results: resultsData,
    loading,
    error,
    step,
    questions,
    showResults,
    setShowResults,
    getNextQuestion,
    getFirstQuestion,
    submitAnswer,
    stepBack,
    setStep,
    saveCustomerAnswers,
    savingAnswer,
    noQuestions,
    setCustomerEmail
  }
}

export const useProductSelectorResults = () => {
  const usesByDay = results[0]?.uses_by_day !== '' ? results[0]?.uses_by_day : '1'
  const hasResults = results?.length >= 1
  const sku = results[0]?.sku
  const product = results[0]?.product
  const [productSku, setProductSku] = useState(sku)

  const [getPromotion, { data }] = useMutation(getPromotionForCart)
  const promotionValue = data?.getPromoToCart?.product?.price_range?.maximum_price?.final_price?.value

  useEffect(() => {
    if (sku && product) {
      getPromotion({ variables: { sku: productSku } })
    }
    // eslint-disable-next-line
  }, [productSku, sku, getPromotion, product])

  const primaryProduct = results.find(result => result.product.sku === productSku)?.product
  const extraProducts = results.filter(result => result.product.sku !== productSku)

  const resultUrl = results?.[0]?.url_key
  const productUrl = resultUrl !== primaryProduct?.url_key ? `${resultUrl}?sku=${productSku}` : primaryProduct?.url_key

  return {
    primaryProduct,
    extraProducts,
    sku,
    usesByDay,
    hasResults,
    results,
    promotionValue,
    productSku,
    setProductSku,
    productUrl
  }
}
